import React, { useState } from 'react';
import {
  Box,
  Text,
  Button,
  Flex,
  Icon,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody
} from '@chakra-ui/react';
import { FaCalendarAlt, FaTrashAlt, FaPlus, FaArrowLeft } from 'react-icons/fa'; // Import FaArrowLeft
import UploadCertificate from '../seeker/Upload_certificate'; // Import the Upload_document component
import { useBreakpointValue } from '@chakra-ui/react'; // Import useBreakpointValue

const DocumentCard = ({ name, date, onDelete }) => {
  const isExpired = new Date(date) < new Date(); // Check if the document is expired

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete the document: ${name}?`)) {
      onDelete(name); // Call the onDelete function passed as a prop
    }
  };

  return (
    <Box
      maxW="sm"
      minH="200px"
      borderWidth="1px"
      borderRadius="lg"
      overflow="hidden"
      p="4"
      boxShadow="md"
      bg="white"
      position="relative"
    >
      {/* Document Title and Date */}
      <Flex justify="space-between" align="center">
        <Text fontWeight="bold" fontSize="lg" textAlign="left" w="full">
          {name}
        </Text>
      </Flex>
      
      <Flex align="center" mt="2">
        <Icon as={FaCalendarAlt} boxSize={4} color="gray.500" />
        <Text fontSize="sm" ml="2" color={isExpired ? "red.500" : "gray.500"} textAlign="left">
          {isExpired ? "Expired" : date}
        </Text>
      </Flex>

      {/* Delete Icon and View Button aligned on the same line */}
      <Flex position="absolute" bottom="4" left="4" right="4" justify="space-between" align="center">
        <Icon
          as={FaTrashAlt}
          color="gray.500"
          boxSize={5}
          cursor="pointer"
          onClick={handleDelete} // Handle delete on click
        />
        <Button
          colorScheme="blue"
          bg="blue.500"
          color="white"
          size="sm"
          width="100px"
          height="32px"
          fontSize="12px"
        >
          View
        </Button>
      </Flex>
    </Box>
  );
};

const DocumentCardList = ({ onBack }) => { // Accept onBack as a prop
  const [isOpen, setIsOpen] = useState(false); // Modal state
  const [documents, setDocuments] = useState([
    // { name: "Document for Hospital Job", date: "2025-12-16" },
    // { name: "Document for Medical Training", date: "2021-01-05" },
    // { name: "Document for Surgery Assistance", date: "2025-03-23" },
    // { name: "Document for Emergency Response", date: "2021-07-12" },
    // { name: "Document for Nursing", date: "2024-11-02" },
    // { name: "Document for Pediatrics", date: "2021-10-14" },
    // { name: "Document for Advanced Medical Care", date: "2023-11-21" },
    // { name: "Document for Clinical Trials", date: "2022-02-03" },
    // { name: "Document for Emergency Medicine", date: "2026-04-19" },
    // { name: "Document for Cardiology", date: "2024-12-07" },
    // { name: "Document for Neurology", date: "2023-09-12" },
    // { name: "Document for Anesthesiology", date: "2022-12-24" }
  ]);

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  const handleDeleteDocument = (name) => {
    setDocuments(documents.filter(doc => doc.name !== name));
  };

  const buttonText = useBreakpointValue({ base: '', md: 'Upload Document' }); // Responsive button text
  const buttonStyle = useBreakpointValue({
    base: { bg: 'transparent', color: 'blue.500', boxSize: 6 },
    md: { bg: 'blue.500', color: 'white', width: '220px', height: '50px' }
  });

  return (
    <Box
      bg="gray.100"
      minH="100vh"
      p={4} // Decrease padding here
      position="relative"
      maxHeight="80vh" // Set a maximum height for the entire component
      overflowY="auto" // Enable vertical scrolling
      borderRadius="md" // Add border radius here
    >
      <Box maxW="1200px" mx="auto" p={2} position="relative"> 
        {/* Back Arrow and Upload Document Button */}
        <Flex justify="space-between" mb={4} align="center">
          <Icon
            as={FaArrowLeft}
            color="blue.500"
            boxSize={4} // Decrease the size of the back arrow
            cursor="pointer"
            onClick={onBack} // Use the onBack function to navigate to MyProfile
          />
          <Button
            colorScheme="blue"
            {...buttonStyle} // Apply responsive styles
            fontSize="md"
            rightIcon={
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                boxSize={6}
                borderWidth="1px"
                borderRadius="md"
                borderColor="gray.500"
              >
                <Icon as={FaPlus} boxSize={4} />
              </Box>
            }
            onClick={openModal} // Open modal on button click
            _hover={{ bg: buttonStyle.bg }} // Maintain the same background color on hover
            _focus={{ boxShadow: "none", bg: "transparent" }} // Remove focus outline and background
          >
            {buttonText} {/* Use responsive button text */}
          </Button>
        </Flex>

        <SimpleGrid columns={{ base: 1, sm: 1, md: 2, lg: 2, xl: 3 }} spacing={3}>
          {documents.map((doc, index) => (
            <Box
              key={index}
              w="100%" // Full width in compact mode
              minH="200px"
              borderWidth="1px"
              borderRadius="lg"
              overflow="hidden"
              p="4"
              boxShadow="md"
              bg="white"
              position="relative"
            >
              {/* Document Title and Date */}
              <Flex justify="space-between" align="center">
                <Text fontWeight="bold" fontSize="lg" textAlign="left" w="full">
                  {doc.name}
                </Text>
              </Flex>
              
              <Flex align="center" mt="2">
                <Icon as={FaCalendarAlt} boxSize={4} color="gray.500" />
                <Text fontSize="sm" ml="2" color={new Date(doc.date) < new Date() ? "red.500" : "gray.500"} textAlign="left">
                  {new Date(doc.date) < new Date() ? "Expired" : doc.date}
                </Text>
              </Flex>

              {/* Delete Icon and View Button aligned on the same line */}
              <Flex position="absolute" bottom="4" left="4" right="4" justify="space-between" align="center">
                <Icon
                  as={FaTrashAlt}
                  color="gray.500"
                  boxSize={5}
                  cursor="pointer"
                  onClick={() => handleDeleteDocument(doc.name)} // Handle delete on click
                />
                <Button
                  colorScheme="blue"
                  bg="blue.500"
                  color="white"
                  size="sm"
                  width="100px"
                  height="32px"
                  fontSize="12px"
                >
                  View
                </Button>
              </Flex>
            </Box>
          ))}
        </SimpleGrid>

        {/* Popup Modal */}
        <Modal isOpen={isOpen} onClose={closeModal} size="xl" closeOnOverlayClick={true}> 
          {/* Modal overlay with blur effect and transparency */}
          <ModalOverlay
            backdropFilter="blur(10px)" // Apply blur effect
            bg="rgba(0, 0, 0, 0.5)" // Semi-transparent black background
          />
          <ModalContent
            bg="transparent" // Ensure the modal content background is transparent
            boxShadow="none" // Remove any shadow that might appear as a background
            maxWidth="500px" // Ensure max width is set for modal
            p={0} // Remove extra padding from ModalContent
          >
            <ModalBody p={4}> {/* Add some padding to ModalBody */}
              <UploadCertificate closeModal={closeModal} /> 
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    </Box>
  );
};

export default DocumentCardList;
